/**
 * Pixel Morph Animation Component
 *
 * A mesmerizing loading animation that shows pixel particles
 * assembling into different creature silhouettes with wave motion
 * and shimmer effects.
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { SILHOUETTE_SHAPES, type SilhouetteShape } from './silhouetteShapes';

interface PixelMorphAnimationProps {
  width?: number;
  height?: number;
  pixelSize?: number;
  /** Duration to hold each shape before morphing (ms) */
  holdDuration?: number;
  /** Duration of the morph transition (ms) */
  morphDuration?: number;
  /** Which shapes to cycle through (defaults to all) */
  shapes?: SilhouetteShape[];
}

interface Particle {
  // Current position
  x: number;
  y: number;
  // Target position in current shape
  targetX: number;
  targetY: number;
  // Previous target (for morphing)
  prevTargetX: number;
  prevTargetY: number;
  // Base color from shape
  baseColor: string;
  // Current rendered color (with shimmer)
  color: string;
  // Random offset for wave motion
  waveOffset: number;
  // Particle size variation
  size: number;
  // Shimmer phase
  shimmerPhase: number;
  // Velocity for scattered state
  vx: number;
  vy: number;
}

// Brand colors
const COLORS = {
  cyan: '#00D4FF',
  cyanLight: '#66E5FF',
  gold: '#FFD700',
  goldLight: '#FFE566',
  magenta: '#FF00FF',
  white: '#FFFFFF',
  darkBg: '#0A1628',
};

export const PixelMorphAnimation: React.FC<PixelMorphAnimationProps> = ({
  width = 200,
  height = 200,
  pixelSize = 4,
  holdDuration = 3000,
  morphDuration = 1500,
  shapes = SILHOUETTE_SHAPES,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>(0);
  const stateRef = useRef({
    currentShapeIndex: 0,
    morphProgress: 0, // 0 = at shape, 1 = fully morphed to next
    phase: 'hold' as 'hold' | 'morph',
    phaseStartTime: 0,
    time: 0,
  });

  // Initialize particles from first shape
  const initializeParticles = useCallback(() => {
    const particles: Particle[] = [];
    const shape = shapes[0];
    const gridWidth = shape.grid[0].length;
    const gridHeight = shape.grid.length;

    // Center the shape in the canvas
    const offsetX = (width - gridWidth * pixelSize) / 2;
    const offsetY = (height - gridHeight * pixelSize) / 2;

    for (let y = 0; y < gridHeight; y++) {
      for (let x = 0; x < gridWidth; x++) {
        if (shape.grid[y][x] === 1) {
          const px = offsetX + x * pixelSize + pixelSize / 2;
          const py = offsetY + y * pixelSize + pixelSize / 2;

          particles.push({
            x: px,
            y: py,
            targetX: px,
            targetY: py,
            prevTargetX: px,
            prevTargetY: py,
            baseColor: shape.color || COLORS.cyan,
            color: shape.color || COLORS.cyan,
            waveOffset: Math.random() * Math.PI * 2,
            size: pixelSize * (0.8 + Math.random() * 0.4),
            shimmerPhase: Math.random() * Math.PI * 2,
            vx: 0,
            vy: 0,
          });
        }
      }
    }

    particlesRef.current = particles;
  }, [shapes, width, height, pixelSize]);

  // Get particle positions for a given shape
  const getShapePositions = useCallback((shapeIndex: number): { x: number; y: number; color: string }[] => {
    const shape = shapes[shapeIndex % shapes.length];
    const gridWidth = shape.grid[0].length;
    const gridHeight = shape.grid.length;
    const offsetX = (width - gridWidth * pixelSize) / 2;
    const offsetY = (height - gridHeight * pixelSize) / 2;

    const positions: { x: number; y: number; color: string }[] = [];

    for (let y = 0; y < gridHeight; y++) {
      for (let x = 0; x < gridWidth; x++) {
        if (shape.grid[y][x] === 1) {
          positions.push({
            x: offsetX + x * pixelSize + pixelSize / 2,
            y: offsetY + y * pixelSize + pixelSize / 2,
            color: shape.color || COLORS.cyan,
          });
        }
      }
    }

    return positions;
  }, [shapes, width, height, pixelSize]);

  // Transition to next shape
  const transitionToNextShape = useCallback(() => {
    const state = stateRef.current;
    const nextShapeIndex = (state.currentShapeIndex + 1) % shapes.length;
    const currentPositions = getShapePositions(state.currentShapeIndex);
    const nextPositions = getShapePositions(nextShapeIndex);

    const particles = particlesRef.current;

    // Match particles to new positions (simple nearest-neighbor for now)
    // Resize particle array if needed
    while (particles.length < nextPositions.length) {
      // Add new particles from random positions
      const randomParticle = particles[Math.floor(Math.random() * particles.length)];
      particles.push({
        ...randomParticle,
        x: width / 2 + (Math.random() - 0.5) * width * 0.5,
        y: height / 2 + (Math.random() - 0.5) * height * 0.5,
        waveOffset: Math.random() * Math.PI * 2,
        shimmerPhase: Math.random() * Math.PI * 2,
      });
    }

    while (particles.length > nextPositions.length) {
      particles.pop();
    }

    // Assign new targets
    // Sort by distance to get better morphing
    const usedIndices = new Set<number>();

    particles.forEach((particle, i) => {
      particle.prevTargetX = particle.targetX;
      particle.prevTargetY = particle.targetY;

      // Find closest unused position
      let bestIndex = 0;
      let bestDist = Infinity;

      for (let j = 0; j < nextPositions.length; j++) {
        if (usedIndices.has(j)) continue;
        const dx = nextPositions[j].x - particle.x;
        const dy = nextPositions[j].y - particle.y;
        const dist = dx * dx + dy * dy;
        if (dist < bestDist) {
          bestDist = dist;
          bestIndex = j;
        }
      }

      usedIndices.add(bestIndex);
      particle.targetX = nextPositions[bestIndex].x;
      particle.targetY = nextPositions[bestIndex].y;
      particle.baseColor = nextPositions[bestIndex].color;
    });

    state.currentShapeIndex = nextShapeIndex;
    state.phase = 'morph';
    state.morphProgress = 0;
    state.phaseStartTime = state.time;
  }, [shapes, getShapePositions, width, height]);

  // Easing function for smooth morphing
  const easeInOutCubic = (t: number): number => {
    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  // Main animation loop
  const animate = useCallback((timestamp: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const state = stateRef.current;
    const particles = particlesRef.current;
    state.time = timestamp;

    // Update phase timing
    const phaseElapsed = timestamp - state.phaseStartTime;

    if (state.phase === 'hold' && phaseElapsed > holdDuration) {
      transitionToNextShape();
    } else if (state.phase === 'morph') {
      state.morphProgress = Math.min(1, phaseElapsed / morphDuration);
      if (state.morphProgress >= 1) {
        state.phase = 'hold';
        state.phaseStartTime = timestamp;
        state.morphProgress = 0;
      }
    }

    // Clear canvas with dark background
    ctx.fillStyle = COLORS.darkBg;
    ctx.fillRect(0, 0, width, height);

    // Update and draw particles
    const easedProgress = easeInOutCubic(state.morphProgress);

    particles.forEach((particle) => {
      // Calculate current position based on morph progress
      if (state.phase === 'morph') {
        particle.x = particle.prevTargetX + (particle.targetX - particle.prevTargetX) * easedProgress;
        particle.y = particle.prevTargetY + (particle.targetY - particle.prevTargetY) * easedProgress;
      } else {
        particle.x = particle.targetX;
        particle.y = particle.targetY;
      }

      // Add wave motion
      const waveTime = timestamp * 0.002;
      const waveX = Math.sin(waveTime + particle.waveOffset) * 2;
      const waveY = Math.cos(waveTime * 0.7 + particle.waveOffset) * 2;

      // Shimmer effect - cycle through colors
      particle.shimmerPhase += 0.02;
      const shimmer = Math.sin(particle.shimmerPhase) * 0.5 + 0.5;

      // Color interpolation for shimmer
      const baseColor = particle.baseColor;
      const shimmerColor = baseColor === COLORS.cyan ? COLORS.cyanLight :
                          baseColor === COLORS.gold ? COLORS.goldLight :
                          COLORS.white;

      // Parse colors for interpolation
      const alpha = 0.7 + shimmer * 0.3;

      // Draw pixel with glow effect
      const drawX = particle.x + waveX;
      const drawY = particle.y + waveY;

      // Outer glow
      ctx.shadowColor = particle.baseColor;
      ctx.shadowBlur = 4 + shimmer * 4;

      // Main pixel
      ctx.fillStyle = particle.baseColor;
      ctx.globalAlpha = alpha;
      ctx.fillRect(
        drawX - particle.size / 2,
        drawY - particle.size / 2,
        particle.size,
        particle.size
      );

      // Bright center
      ctx.globalAlpha = shimmer * 0.5;
      ctx.fillStyle = shimmerColor;
      ctx.fillRect(
        drawX - particle.size / 4,
        drawY - particle.size / 4,
        particle.size / 2,
        particle.size / 2
      );

      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
    });

    // Add some floating sparkles
    const sparkleCount = 5;
    for (let i = 0; i < sparkleCount; i++) {
      const sparkleTime = timestamp * 0.001 + i * 1.5;
      const sparkleX = width / 2 + Math.sin(sparkleTime * 0.7) * width * 0.4;
      const sparkleY = height / 2 + Math.cos(sparkleTime * 0.5) * height * 0.3;
      const sparkleAlpha = (Math.sin(sparkleTime * 3) * 0.5 + 0.5) * 0.6;
      const sparkleSize = 2 + Math.sin(sparkleTime * 2) * 1;

      ctx.fillStyle = i % 2 === 0 ? COLORS.cyan : COLORS.gold;
      ctx.globalAlpha = sparkleAlpha;
      ctx.beginPath();
      ctx.arc(sparkleX, sparkleY, sparkleSize, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    animationRef.current = requestAnimationFrame(animate);
  }, [width, height, holdDuration, morphDuration, transitionToNextShape]);

  // Initialize and start animation
  useEffect(() => {
    initializeParticles();
    stateRef.current.phaseStartTime = performance.now();
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationRef.current);
    };
  }, [initializeParticles, animate]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        display: 'block',
        borderRadius: '8px',
      }}
    />
  );
};

export default PixelMorphAnimation;
